import React, { useState, useEffect } from 'react';
import AuthImage from '@/components/AuthImage';
import { motion } from 'framer-motion';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { 
  ArrowLeft, Loader2, AlertTriangle, ThumbsUp, ThumbsDown, 
  RefreshCw, Leaf, FileText
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Color scheme for micronutrients
const NUTRIENT_COLORS = {
  vitamin_a: { gradient: 'from-orange-400 to-orange-600', bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200' },
  vitamin_c: { gradient: 'from-yellow-400 to-yellow-600', bg: 'bg-yellow-50', text: 'text-yellow-700', border: 'border-yellow-200' },
  vitamin_b12: { gradient: 'from-red-400 to-red-600', bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200' },
  folate: { gradient: 'from-green-400 to-green-600', bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200' },
  calcium: { gradient: 'from-blue-400 to-blue-600', bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200' },
  iron: { gradient: 'from-purple-400 to-purple-600', bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-200' },
  potassium: { gradient: 'from-cyan-400 to-cyan-600', bg: 'bg-cyan-50', text: 'text-cyan-700', border: 'border-cyan-200' },
  iodine: { gradient: 'from-pink-400 to-pink-600', bg: 'bg-pink-50', text: 'text-pink-700', border: 'border-pink-200' },
  magnesium: { gradient: 'from-teal-400 to-teal-600', bg: 'bg-teal-50', text: 'text-teal-700', border: 'border-teal-200' },
  phosphorus: { gradient: 'from-violet-400 to-violet-600', bg: 'bg-violet-50', text: 'text-violet-700', border: 'border-violet-200' }
};

const MicronutrientDetailPage = () => {
  const navigate = useNavigate();
  const { nutrientId } = useParams();
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [detailData, setDetailData] = useState(null);

  useEffect(() => {
    if (nutrientId) {
      fetchNutrientDetail();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nutrientId]);

  const fetchNutrientDetail = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/myinsul/wellness-markers/micronutrient/${nutrientId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDetailData(response.data);
    } catch (error) {
      toast.error('Failed to load micronutrient details');
      if (error.response?.status === 400) {
        navigate('/myinsul/wellness-markers');
      }
    } finally {
      setLoading(false);
    }
  };

  const colors = NUTRIENT_COLORS[nutrientId] || { 
    gradient: 'from-gray-400 to-gray-600', 
    bg: 'bg-gray-50', 
    text: 'text-gray-700',
    border: 'border-gray-200'
  };

  const nutrient = detailData?.nutrient;

  return (
    <div className={`min-h-screen bg-gradient-to-br ${colors.bg} via-white to-white`}>
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-lg border-b border-border/50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => navigate('/myinsul/wellness-markers')}
                className="rounded-full"
                data-testid="back-btn"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div className="flex items-center gap-2">
                <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${colors.gradient} flex items-center justify-center`}>
                  <Leaf className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className={`text-xl font-bold bg-gradient-to-r ${colors.gradient} bg-clip-text text-transparent`}>
                    {nutrient?.name || 'Loading...'}
                  </h1>
                  <p className="text-xs text-muted-foreground">Food Analysis</p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={fetchNutrientDetail}
                disabled={loading}
                className="rounded-full"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              </Button>
              <span className={`text-xs ${colors.bg} ${colors.text} px-3 py-1 rounded-full font-medium ${colors.border} border`}>
                Past 30 Days
              </span>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className={`w-8 h-8 animate-spin ${colors.text}`} />
          </div>
        ) : !detailData?.has_data ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-2xl shadow-lg p-8 text-center"
          >
            <AlertTriangle className={`w-16 h-16 mx-auto ${colors.text} opacity-50 mb-4`} />
            <h2 className="text-xl font-bold mb-2">No Data Available</h2>
            <p className="text-muted-foreground">{detailData?.message || 'No food entries found for analysis.'}</p>
            <Button 
              onClick={() => navigate('/myinsul/wellness-markers')}
              className="mt-4 rounded-full"
            >
              Back to Wellness Markers
            </Button>
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            {/* Nutrient Info Card */}
            <div className={`bg-gradient-to-r ${colors.gradient} rounded-2xl p-6 text-white`}>
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold">{nutrient?.name}</h2>
                  <p className="text-white/80 mt-1">
                    Recommended Daily Intake: <strong>{nutrient?.recommended} {nutrient?.unit}</strong>
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-white/70">Analysis Period</p>
                  <p className="font-medium">
                    {detailData.date_range?.start} to {detailData.date_range?.end}
                  </p>
                  <p className="text-sm text-white/70 mt-1">{detailData.total_entries} meals analyzed</p>
                </div>
              </div>
            </div>

            {/* Top and Bottom Foods Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Top 5 Foods (High in nutrient) */}
              <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
                <div className={`${colors.bg} ${colors.border} border-b px-6 py-4`}>
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${colors.gradient} flex items-center justify-center`}>
                      <ThumbsUp className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold">Top 5 {nutrient?.name}-Rich Foods</h3>
                      <p className="text-sm text-muted-foreground">Highest {nutrient?.name} content from your meals</p>
                    </div>
                  </div>
                </div>
                
                <div className="p-4">
                  {detailData.top_foods?.length > 0 ? (
                    <div className="space-y-3">
                      {detailData.top_foods.map((food, index) => (
                        <motion.div
                          key={`top-${index}`}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: index * 0.1 }}
                          className={`flex items-center gap-4 p-3 rounded-xl ${colors.bg} ${colors.border} border`}
                        >
                          <div className={`w-8 h-8 rounded-full bg-gradient-to-br ${colors.gradient} text-white flex items-center justify-center font-bold flex-shrink-0`}>
                            {index + 1}
                          </div>
                          {food.image_url ? (
                            <AuthImage 
                              src={food.image_url} 
                              alt={food.name}
                              className="w-12 h-12 rounded-lg object-cover flex-shrink-0"
                            />
                          ) : (
                            <div className={`w-12 h-12 rounded-lg ${colors.bg} flex items-center justify-center flex-shrink-0`}>
                              <FileText className={`w-6 h-6 ${colors.text}`} />
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <h4 className="font-semibold truncate" title={food.name}>{food.name}</h4>
                            <div className="flex items-center gap-2 text-xs text-muted-foreground">
                              <span className={`${colors.bg} ${colors.text} px-2 py-0.5 rounded-full`}>
                                {food.meal_type}
                              </span>
                              <span>{food.date}</span>
                            </div>
                          </div>
                          <div className={`text-right flex-shrink-0 ${colors.text}`}>
                            <span className="text-lg font-bold">{food.value}</span>
                            <span className="text-xs ml-1">{nutrient?.unit}</span>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-muted-foreground">
                      <ThumbsUp className="w-12 h-12 mx-auto mb-2 opacity-30" />
                      <p>No high {nutrient?.name} foods found</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Bottom 5 Foods (Low in nutrient) */}
              <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
                <div className="bg-gray-50 border-b border-gray-200 px-6 py-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-gray-400 to-gray-600 flex items-center justify-center">
                      <ThumbsDown className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold">Bottom 5 {nutrient?.name} Foods</h3>
                      <p className="text-sm text-muted-foreground">Lowest {nutrient?.name} content from your meals</p>
                    </div>
                  </div>
                </div>
                
                <div className="p-4">
                  {detailData.bottom_foods?.length > 0 ? (
                    <div className="space-y-3">
                      {detailData.bottom_foods.map((food, index) => (
                        <motion.div
                          key={`bottom-${index}`}
                          initial={{ opacity: 0, x: 20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: index * 0.1 }}
                          className="flex items-center gap-4 p-3 rounded-xl bg-gray-50 border border-gray-200"
                        >
                          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-gray-400 to-gray-600 text-white flex items-center justify-center font-bold flex-shrink-0">
                            {index + 1}
                          </div>
                          {food.image_url ? (
                            <AuthImage 
                              src={food.image_url} 
                              alt={food.name}
                              className="w-12 h-12 rounded-lg object-cover flex-shrink-0"
                            />
                          ) : (
                            <div className="w-12 h-12 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                              <FileText className="w-6 h-6 text-gray-400" />
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <h4 className="font-semibold truncate" title={food.name}>{food.name}</h4>
                            <div className="flex items-center gap-2 text-xs text-muted-foreground">
                              <span className="bg-gray-200 text-gray-600 px-2 py-0.5 rounded-full">
                                {food.meal_type}
                              </span>
                              <span>{food.date}</span>
                            </div>
                          </div>
                          <div className="text-right flex-shrink-0 text-gray-600">
                            <span className="text-lg font-bold">{food.value}</span>
                            <span className="text-xs ml-1">{nutrient?.unit}</span>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-muted-foreground">
                      <ThumbsDown className="w-12 h-12 mx-auto mb-2 opacity-30" />
                      <p>No low {nutrient?.name} foods found</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Back to Wellness Markers Button */}
            <div className="text-center pt-4">
              <Button
                onClick={() => navigate('/myinsul/wellness-markers')}
                variant="outline"
                className="rounded-full px-6"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to All Wellness Markers
              </Button>
            </div>
          </motion.div>
        )}
      </main>
    </div>
  );
};

export default MicronutrientDetailPage;
